import { useEffect, useState } from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';
import Modal from '@/components/ui/Modal';
import { api } from '@/lib/api';
import { formatDate } from '@/lib/utils';
import { Gasto, formatImporte } from './tipos';

interface Props {
  abierto: boolean;
  gasto: Gasto | null;
  onCerrar: () => void;
  onCobrado: () => void;
}

/**
 * Marcar un gasto como cobrado, apuntando cuánto se repercutió de verdad.
 *
 * El importe se pregunta en vez de darlo por hecho porque no siempre se le pasa
 * al propietario lo que costó: hay gastos que se asumen a medias y otros que se
 * repercuten redondeados. El importe original se queda en `amount` y lo cobrado
 * en `chargedAmount`, y la lista enseña los dos.
 */
export default function ModalCobrar({ abierto, gasto, onCerrar, onCobrado }: Props) {
  const [importe, setImporte] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!abierto || !gasto) return;
    // Se propone el importe del gasto: lo normal es cobrarlo entero, y así el
    // caso habitual es confirmar sin teclear.
    setImporte(gasto.amount ? String(gasto.amount) : '0');
    setError(null);
  }, [abierto, gasto]);

  if (!gasto) return null;

  const cobrar = async (e: React.FormEvent) => {
    e.preventDefault();
    const valor = Number(importe);
    if (!Number.isFinite(valor) || valor < 0) {
      return setError('El importe cobrado debe ser un número mayor o igual que 0');
    }

    setGuardando(true);
    setError(null);
    try {
      await api.cobrarGasto(gasto.id, gasto.propertyId, valor);
      onCobrado();
      onCerrar();
    } catch (err: any) {
      setError(err?.message || 'No se ha podido marcar el gasto como cobrado');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal isOpen={abierto} onClose={onCerrar} title="Marcar gasto como cobrado" size="md">
      <form onSubmit={cobrar} className="space-y-4">
        <dl className="rounded-lg bg-gray-50 border border-gray-100 p-3 text-sm space-y-1">
          <div className="flex justify-between gap-3">
            <dt className="text-sereno-dark/50">Propiedad</dt>
            <dd className="font-medium text-right">{gasto.propertyName}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-sereno-dark/50">Concepto</dt>
            <dd className="font-medium text-right">{gasto.concept}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-sereno-dark/50">Fecha</dt>
            <dd className="font-medium text-right">{formatDate(gasto.expenseDate)}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-sereno-dark/50">Importe del gasto</dt>
            <dd className="font-medium text-right">{formatImporte(gasto.amount)}</dd>
          </div>
        </dl>

        <div>
          <label className="label" htmlFor="gasto-cobrado">Importe cobrado (€)</label>
          <input
            id="gasto-cobrado"
            type="number"
            step="0.01"
            min="0"
            className="input min-h-11"
            value={importe}
            onChange={(e) => setImporte(e.target.value)}
            disabled={guardando}
            autoFocus
          />
          <p className="mt-1 text-xs text-sereno-dark/40">
            Lo que se le repercute al propietario, si no es el importe completo.
          </p>
        </div>

        {error && (
          <div className="flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">
            <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Mismo criterio que en ModalGasto: primaria arriba en el móvil y
            Cancelar bajo el pulgar, para no cobrar un gasto de un roce. */}
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
          <button type="button" onClick={onCerrar} disabled={guardando} className="btn-ghost min-h-11 text-sm">
            Cancelar
          </button>
          <button type="submit" disabled={guardando} className="btn-primary min-h-11 text-sm flex items-center justify-center gap-2">
            {guardando && <Loader2 size={15} className="animate-spin" />}
            Marcar como cobrado
          </button>
        </div>
      </form>
    </Modal>
  );
}
